import React, { useMemo, useState } from "react";
import { Box, Button, Grid, HStack, Heading, Text, VStack } from "@chakra-ui/react";
import { CheckCircle2, Download, FileCode2, GitBranch, PackageCheck } from "lucide-react";

import useAbilityPermissions from "../hooks/useAbilityPermissions";
import NoPermissions from "./NoPermissions";
import updatesLedger from "../data/reportUpdates.json";

const typeLabels = {
  data: "Data",
  export: "Export",
  governance: "Governance",
  "report-output": "Report output",
  workflow: "Workflow",
};

const typeStyles = {
  data: { bg: "#e8f0fe", color: "#174ea6" },
  export: { bg: "#e6f4ea", color: "#137333" },
  governance: { bg: "#fef7e0", color: "#a05a00" },
  "report-output": { bg: "#fce8e6", color: "#c5221f" },
  workflow: { bg: "#f3e8fd", color: "#681da8" },
};

const csvEscape = (value) => `"${String(value || "").replace(/"/g, '""')}"`;

const downloadTextFile = ({ filename, type, contents }) => {
  const blob = new Blob([contents], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const buildUpdatesCsv = (updates) => {
  const headers = [
    "Date",
    "Type",
    "Status",
    "Title",
    "Affected Repos",
    "Affected Features",
    "Admin Impact",
  ];

  const rows = updates.map((update) => [
    update.date,
    typeLabels[update.type] || update.type,
    update.status,
    update.title,
    update.affectedRepos.join("; "),
    update.affectedFeatures.join("; "),
    update.adminImpact,
  ]);

  return [headers, ...rows].map((row) => row.map(csvEscape).join(",")).join("\n");
};

const FilterSelect = ({ label, value, onChange, children }) => (
  <Box>
    <Text fontSize="12px" fontWeight="700" color="var(--gray)" mb="5px">
      {label}
    </Text>
    <Box
      as="select"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      border="1px solid"
      borderColor="var(--borderGrey)"
      borderRadius="6px"
      p="8px 10px"
      minW="180px"
      fontSize="14px"
      bg="white"
    >
      {children}
    </Box>
  </Box>
);

const Stat = ({ icon: Icon, label, value }) => (
  <Box border="1px solid" borderColor="var(--borderGrey)" borderRadius="8px" p="14px" bg="white">
    <HStack gap="10px">
      <Box
        w="34px"
        h="34px"
        borderRadius="6px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="#f8f9fa"
        color="var(--blue)"
      >
        <Icon size={17} />
      </Box>
      <Box>
        <Text fontSize="19px" fontWeight="700" color="var(--black-dry)" lineHeight="1.1">
          {value}
        </Text>
        <Text fontSize="12px" color="var(--gray)">
          {label}
        </Text>
      </Box>
    </HStack>
  </Box>
);

const UpdateCard = ({ update }) => {
  const style = typeStyles[update.type] || typeStyles.governance;

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="var(--borderGrey)"
      borderRadius="8px"
      p="18px"
      data-update-id={update.id}
    >
      <HStack justify="space-between" align="flex-start" gap="14px" mb="10px">
        <Box minW={0}>
          <HStack gap="8px" mb="7px" flexWrap="wrap">
            <Box
              px="8px"
              py="3px"
              borderRadius="4px"
              bg={style.bg}
              color={style.color}
              fontSize="11px"
              fontWeight="700"
            >
              {typeLabels[update.type] || update.type}
            </Box>
            <Text fontSize="12px" color="var(--lightGrey)">
              {update.date}
            </Text>
          </HStack>
          <Heading as="h2" fontSize="18px" color="var(--black-dry)">
            {update.title}
          </Heading>
        </Box>
        <Box
          px="8px"
          py="3px"
          borderRadius="4px"
          bg="#e6f4ea"
          color="#137333"
          fontSize="11px"
          fontWeight="700"
          textTransform="capitalize"
          flexShrink={0}
        >
          {update.status}
        </Box>
      </HStack>

      <Text fontSize="14px" color="var(--darkGrey)" lineHeight="1.6" mb="14px">
        {update.summary}
      </Text>

      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap="16px">
        <Box>
          <Text fontSize="12px" fontWeight="700" color="var(--gray-45)" mb="7px">
            Affected Repos
          </Text>
          <HStack gap="6px" flexWrap="wrap">
            {update.affectedRepos.map((repo) => (
              <Box
                key={repo}
                px="7px"
                py="3px"
                borderRadius="4px"
                border="1px solid"
                borderColor="var(--borderGrey)"
                color="var(--gray)"
                fontSize="11px"
                fontFamily="monospace"
              >
                {repo}
              </Box>
            ))}
          </HStack>
        </Box>

        <Box>
          <Text fontSize="12px" fontWeight="700" color="var(--gray-45)" mb="7px">
            Affected Features
          </Text>
          <HStack gap="6px" flexWrap="wrap">
            {update.affectedFeatures.map((feature) => (
              <Box
                key={feature}
                px="7px"
                py="3px"
                borderRadius="4px"
                bg="#f8f9fa"
                color="var(--gray)"
                fontSize="11px"
              >
                {feature}
              </Box>
            ))}
          </HStack>
        </Box>
      </Grid>

      <Box mt="14px" pt="14px" borderTop="1px solid" borderColor="var(--borderGrey)">
        <Text fontSize="12px" fontWeight="700" color="var(--gray-45)" mb="5px">
          Admin Impact
        </Text>
        <Text fontSize="14px" color="var(--darkGrey)" lineHeight="1.6">
          {update.adminImpact}
        </Text>
      </Box>

      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap="16px" mt="14px">
        <Box>
          <Text fontSize="12px" fontWeight="700" color="var(--gray-45)" mb="7px">
            Source References
          </Text>
          <VStack gap="4px" align="stretch">
            {update.sourceReferences.map((file) => (
              <Text key={file} fontSize="11px" color="var(--gray)" fontFamily="monospace">
                {file}
              </Text>
            ))}
          </VStack>
        </Box>
        <Box>
          <Text fontSize="12px" fontWeight="700" color="var(--gray-45)" mb="7px">
            Verification
          </Text>
          <VStack gap="5px" align="stretch">
            {update.verification.map((item) => (
              <HStack key={item} gap="7px" align="flex-start">
                <Box color="var(--green)" mt="2px">
                  <CheckCircle2 size={13} />
                </Box>
                <Text fontSize="12px" color="var(--gray)">
                  {item}
                </Text>
              </HStack>
            ))}
          </VStack>
        </Box>
      </Grid>
    </Box>
  );
};

function Updates() {
  const { isSuperAdmin } = useAbilityPermissions();
  const [repoFilter, setRepoFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const updates = updatesLedger.updates;
  const repoOptions = useMemo(
    () => [...new Set(updates.flatMap((update) => update.affectedRepos))].sort(),
    [updates]
  );
  const typeOptions = useMemo(() => [...new Set(updates.map((update) => update.type))].sort(), [
    updates,
  ]);

  const filteredUpdates = updates.filter((update) => {
    const matchesRepo = repoFilter === "all" || update.affectedRepos.includes(repoFilter);
    const matchesType = typeFilter === "all" || update.type === typeFilter;
    return matchesRepo && matchesType;
  });

  const sourceFileCount = new Set(updates.flatMap((update) => update.sourceReferences)).size;
  const readyCount = updates.filter((update) => update.status === "ready").length;

  if (!isSuperAdmin) {
    return <NoPermissions />;
  }

  return (
    <Box w="100%" maxW="1180px">
      <HStack justify="space-between" align="flex-start" gap="16px" mb="18px" flexWrap="wrap">
        <Box>
          <Heading size="lg" color="var(--black-dry)">
            Updates
          </Heading>
          <Text color="var(--gray)" mt="5px">
            {updatesLedger.description}
          </Text>
        </Box>
        <HStack gap="8px" flexWrap="wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              downloadTextFile({
                filename: "qmr-report-updates.json",
                type: "application/json",
                contents: JSON.stringify({ ...updatesLedger, updates: filteredUpdates }, null, 2),
              })
            }
          >
            <Download size={15} />
            Export JSON
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              downloadTextFile({
                filename: "qmr-report-updates.csv",
                type: "text/csv",
                contents: buildUpdatesCsv(filteredUpdates),
              })
            }
          >
            <Download size={15} />
            Export CSV
          </Button>
        </HStack>
      </HStack>

      <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap="12px" mb="18px">
        <Stat icon={PackageCheck} label="Recorded updates" value={updates.length} />
        <Stat icon={GitBranch} label="Ready updates" value={readyCount} />
        <Stat icon={FileCode2} label="Source references" value={sourceFileCount} />
      </Grid>

      <HStack gap="12px" align="flex-end" flexWrap="wrap" mb="18px">
        <FilterSelect label="Repository" value={repoFilter} onChange={setRepoFilter}>
          <option value="all">All repositories</option>
          {repoOptions.map((repo) => (
            <option key={repo} value={repo}>
              {repo}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect label="Update type" value={typeFilter} onChange={setTypeFilter}>
          <option value="all">All update types</option>
          {typeOptions.map((type) => (
            <option key={type} value={type}>
              {typeLabels[type] || type}
            </option>
          ))}
        </FilterSelect>
        <Text fontSize="13px" color="var(--gray)">
          Showing {filteredUpdates.length} of {updates.length} updates
        </Text>
      </HStack>

      <VStack gap="12px" align="stretch">
        {filteredUpdates.map((update) => (
          <UpdateCard key={update.id} update={update} />
        ))}
      </VStack>
    </Box>
  );
}

export { buildUpdatesCsv };
export default Updates;
